import { useState } from "react";
import { BAND_WIDTH_MAX_MM, ENGAGEMENT_WIDTH_MAX_MM, UK_RING_SIZES } from "../../../shared/api-contract";
import { Chip, Field, Icon, SectionLabel, Select, Slider, Toggle } from "../../kit";
import { useCaps } from "../../editor/CapsProvider";
import { useRing } from "../ringContext";
import { SizeHelpModal } from "../SizeHelpModal";
import { usQuarterSizes } from "../cnc/cncFormat";

/** Ported verbatim from band/steps/DimensionsStep.tsx. */
const WIDTH_PRESETS = [
  { label: "Slim", value: 2.5 },
  { label: "Classic", value: 4 },
  { label: "Bold", value: 6 },
  { label: "Statement", value: 8 },
];

/** Name the width the nearest preset chip already carries, so the slider readout
 * and the chip row can never disagree about what "Bold" means. */
const widthTier = (mm: number) =>
  WIDTH_PRESETS.reduce((a, b) => (Math.abs(b.value - mm) < Math.abs(a.value - mm) ? b : a)).label;

const WIDTH_MIN = 1.5;
/**
 * The reference's width ruler stops at 7.00 mm in 0.50 steps, and stretching
 * the track to the engine's 12 mm limit squeezed every wedding-band width into
 * its left half. So the slider spans the reference range, and the 7–12 mm cuff
 * reach stays one click away: the `Statement` chip (8 mm) puts the value above
 * 7, which lifts the cap so the slider can trim it (see `widthMax` below).
 */
const REF_WIDTH_MAX = 7;
const REF_WIDTH_TICKS = Array.from({ length: 12 }, (_, i) => WIDTH_MIN + i * 0.5);
/** Reference thickness range — also what `thicknessForWidth` already treats as
 * the castable band, so the manual slider no longer offers more than the auto
 * path can produce. */
const THICK_MIN = 1;
const THICK_MAX = 3;
/**
 * The two maxima live in the contract so this slider and the engine clamp cannot
 * drift: with the head attached the band IS the engagement shank, and a shank
 * that wide cannot be cast under a head. `BandConfigurator.setWidth` and
 * `setEngagementEnabled` enforce the same limit, so the value is safe however it
 * arrives — this control just avoids offering a range the engine will reject.
 */
const BAND_WIDTH_MAX = BAND_WIDTH_MAX_MM;
export const ENGAGEMENT_WIDTH_MAX = ENGAGEMENT_WIDTH_MAX_MM;

/**
 * Size tab — the band's finger size and cross-section dimensions.
 *
 * These are the *shared* controls: the engagement shank has no size or width of
 * its own (EngagementBlock deliberately omits them, see the ShankConfig note in
 * the contract), so the engagement builder's "Band Width" / "Ring Size (US)"
 * sliders are the same two controls as the band's — represented once, here.
 * One thing does NOT carry over: the legal width range, which is the band's
 * 1.5–12 mm only while the head is detached (see `ENGAGEMENT_WIDTH_MAX`).
 *
 * Every method used is part of the band half of RingBuilderApi and predates
 * v37, so nothing on this tab needs a caps gate beyond the route-level one.
 */
/** Nearest British letter for an inner diameter — the UK scale's steps do not
 * line up with the US table, so the dropdown shows the closest letter rather
 * than pretending the two are the same ladder. */
function nearestUK(diameterMM: number): string {
  let best = UK_RING_SIZES[0];
  for (const u of UK_RING_SIZES) {
    if (Math.abs(u.diameterMM - diameterMM) < Math.abs(best.diameterMM - diameterMM)) best = u;
  }
  return best.label;
}

/** "10¾" reads the way a jeweller writes it; the value stays the plain number. */
function usSizeLabel(size: number): string {
  const whole = Math.floor(size);
  const frac = Math.round((size - whole) * 100) / 100;
  const glyph = frac === 0.25 ? "¼" : frac === 0.5 ? "½" : frac === 0.75 ? "¾" : null;
  return glyph ? `US ${whole}${glyph}` : `US ${+size.toFixed(2)}`;
}

export function SizeTab() {
  const { state, catalogs, r, apply, engagementOn } = useRing();
  const caps = useCaps();
  const [helpOpen, setHelpOpen] = useState(false);

  // `BandConfigurator.setEngagementEnabled` pulls an over-wide band in the moment
  // the head is attached, so state.widthMM is already legal by the time it gets
  // here — this only keeps the slider from offering a range the engine rejects.
  const maxWidth = engagementOn ? ENGAGEMENT_WIDTH_MAX : BAND_WIDTH_MAX;
  // The viewer computes this from the same ringSizeToDiameter the lathe uses, so
  // it stays honest for the fractional US sizes a UK letter resolves to.
  const innerMM =
    state.spec?.innerDiameterMM ??
    catalogs.ringSizes.find((s) => s.size === state.ringSizeUS)?.diameterMM ??
    0;
  const autoThickness = state.autoThickness ?? false;
  // Ruler over the reference range, uncapped once a chip (or a restored design)
  // takes the band past it — so a wide value stays editable instead of being
  // silently clamped by the input's max.
  const wideBand = state.widthMM > REF_WIDTH_MAX;
  const widthMax = wideBand ? maxWidth : Math.min(maxWidth, REF_WIDTH_MAX);
  // Same courtesy for a design saved under the old 0.80–4.00 thickness slider.
  const thickMin = Math.min(THICK_MIN, state.thicknessMM);
  const thickMax = Math.max(THICK_MAX, state.thicknessMM);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-4">
        <Field label="Ring Size (US)">
          <Select
            value={String(state.ringSizeUS)}
            options={usQuarterSizes(catalogs.ringSizes.map((s) => s.size), state.ringSizeUS).map((size) => ({
              id: String(size),
              label: usSizeLabel(size),
            }))}
            onChange={(v) => void apply(r.setRingSize(parseFloat(v)))}
          />
        </Field>
        <Field label="Ring Size (EU)">
          <Select
            value={String(state.ringSizeEU)}
            options={[
              ...catalogs.ringSizes.map((s) => ({
                id: String(s.eu),
                label: `EU ${s.eu} (Ø ${s.diameterMM}mm)`,
              })),
              // A quarter US size lands between catalogue rows; its EU size is the
              // bore's circumference, which may not be a row — show it, don't
              // let the select fall back to the first option.
              ...(catalogs.ringSizes.some((s) => s.eu === state.ringSizeEU)
                ? []
                : [{ id: String(state.ringSizeEU), label: `EU ${state.ringSizeEU}${innerMM > 0 ? ` (Ø ${innerMM.toFixed(1)}mm)` : ""}` }]),
            ].sort((a, b) => Number(a.id) - Number(b.id))}
            onChange={(v) => void apply(r.setRingSizeEU(parseInt(v)))}
          />
        </Field>
        {caps.ringGeometryV38 ? (
          <Field label="Ring Size (UK)">
            <Select
              value={nearestUK(innerMM)}
              options={UK_RING_SIZES.map((u) => ({ id: u.label, label: `UK ${u.label}` }))}
              onChange={(v) => void apply(r.setRingSizeUK(v))}
            />
          </Field>
        ) : null}
      </div>
      <div className="-mt-3 flex flex-wrap items-center justify-between gap-2">
        {innerMM > 0 ? (
          <span className="font-body text-[11px] tabular-nums text-on-surface-variant">
            {/* The engine's own number, not π·Ø recomputed off an already-rounded
                diameter — double rounding made this read 0.1 mm under the
                Specification panel's copy of the same quantity. */}
            Ø {innerMM.toFixed(1)} mm inner ·{" "}
            {(state.spec?.circumferenceMM ?? innerMM * Math.PI).toFixed(1)} mm circumference
          </span>
        ) : <span />}
        <button
          type="button"
          onClick={() => setHelpOpen(true)}
          className="flex items-center gap-1 font-display text-[10px] font-semibold uppercase tracking-[0.1em] text-secondary transition-colors hover:text-primary"
        >
          <Icon name="help" size={14} />
          I don&rsquo;t know the size
        </button>
      </div>
      {helpOpen ? <SizeHelpModal onClose={() => setHelpOpen(false)} /> : null}

      <SectionLabel>Width</SectionLabel>
      <div className="flex flex-wrap gap-2">
        {WIDTH_PRESETS.filter((p) => p.value <= maxWidth).map((p) => (
          <Chip
            key={p.label}
            selected={Math.abs(state.widthMM - p.value) < 0.05}
            onClick={() => void apply(r.setWidth(p.value))}
            className="flex-1"
          >
            {p.label}
          </Chip>
        ))}
      </div>

      {/* Sliders draft-batch themselves (kit/draft) — no onDragStart/onDragEnd. */}
      <Slider
        label="Ring Width"
        min={WIDTH_MIN}
        max={widthMax}
        step={0.1}
        value={state.widthMM}
        display={(v) => `${widthTier(v)} · ${v.toFixed(2)} mm`}
        minLabel="Thin"
        maxLabel="Bold"
        // Past 7 mm the numbered ruler would need 22 labels, so up there it
        // falls back to marking the preset chips above.
        ticks={wideBand ? WIDTH_PRESETS.map((p) => p.value) : REF_WIDTH_TICKS}
        tickLabels={wideBand ? undefined : (v) => v.toFixed(2)}
        onChange={(v) => void r.setWidth(v)}
      />
      {engagementOn ? (
        <span className="-mt-3 font-body text-[10px] text-outline">
          Capped at {ENGAGEMENT_WIDTH_MAX} mm while the engagement head is attached.
        </span>
      ) : maxWidth > REF_WIDTH_MAX && !wideBand ? (
        <span className="-mt-3 font-body text-[10px] text-outline">
          Wider than {REF_WIDTH_MAX} mm? Pick Statement — the slider then reaches {maxWidth} mm.
        </span>
      ) : null}
      {caps.ringGeometryV38 ? (
        <div className="flex items-center justify-between gap-2">
          <span className="font-display text-[11px] font-medium uppercase tracking-[0.12em] text-on-surface-variant">
            Match Thickness to Width
          </span>
          <Toggle
            checked={autoThickness}
            onChange={(v) => void apply(r.setAutoThickness(v))}
            label="Drive thickness from width"
          />
        </div>
      ) : null}
      <Slider
        label="Ring Thickness"
        min={thickMin}
        max={thickMax}
        step={0.1}
        value={state.thicknessMM}
        display={(v) => `${v.toFixed(2)} mm`}
        disabled={autoThickness}
        minLabel="Slim"
        maxLabel="Solid"
        ticks={5}
        tickLabels={(v) => v.toFixed(2)}
        onChange={(v) => void r.setThickness(v)}
      />
      {autoThickness ? (
        <span className="-mt-3 font-body text-[10px] text-outline">
          Following the width at half its value (1–3 mm). Turn the match off to set it by hand.
        </span>
      ) : null}
    </div>
  );
}
